import { useEffect, useRef, useState } from "react";
import { Check, X, Minus, Crown, Info } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useIsMobile } from "@/hooks/use-mobile";

const ComparisonTable = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const comparisonData = [
    {
      feature: "Upfront Cost",
      tooltip: "The initial investment required to get your website built and launched.",
      waveWire: "$1K – $5K",
      agency: "$5K – $50K+",
      diy: "$0 – $500",
    },
    {
      feature: "Monthly Fees",
      tooltip: "Ongoing costs for hosting, maintenance, and support.",
      waveWire: "$75/mo (optional)",
      agency: "$200+/mo (required)",
      diy: "$15 – $50/mo",
    },
    {
      feature: "5-Year Total",
      tooltip: "The real cost of ownership over 5 years.",
      waveWire: "$1.5K – $8.6K",
      agency: "$17K – $110K+",
      diy: "$900 – $3.5K",
      highlight: true,
    },
    {
      feature: "You Own It",
      tooltip: "Full ownership means you get all code files and can host anywhere.",
      waveWire: true,
      agency: false,
      diy: false,
    },
    {
      feature: "Custom Design",
      tooltip: "A unique design tailored specifically to your brand.",
      waveWire: true,
      agency: true,
      diy: false,
    },
    {
      feature: "Custom Code",
      tooltip: "Clean, hand-written code that can be modified without limitations.",
      waveWire: true,
      agency: true,
      diy: false,
    },
    {
      feature: "Pro Quality",
      tooltip: "Design that meets industry standards for aesthetics and usability.",
      waveWire: true,
      agency: true,
      diy: "partial",
    },
    {
      feature: "Fast Turnaround",
      tooltip: "Time from project start to website launch.",
      waveWire: "1–3 wks",
      agency: "2–6 mos",
      diy: "DIY",
    },
    {
      feature: "SEO Optimized",
      tooltip: "Built with search engine optimization best practices.",
      waveWire: true,
      agency: true,
      diy: "partial",
    },
    {
      feature: "Mobile Ready",
      tooltip: "Your website looks great on all devices.",
      waveWire: true,
      agency: true,
      diy: true,
    },
    {
      feature: "No Contracts",
      tooltip: "Transparent pricing with no surprise fees or auto-renewals.",
      waveWire: true,
      agency: false,
      diy: true,
    },
    {
      feature: "Direct Support",
      tooltip: "Direct access to a real person who knows your project.",
      waveWire: true,
      agency: true,
      diy: false,
    },
    {
      feature: "Switch Hosts",
      tooltip: "Freedom to move your website to any hosting provider.",
      waveWire: true,
      agency: false,
      diy: false,
    },
    {
      feature: "Performance",
      tooltip: "Fast-loading pages with optimized code.",
      waveWire: true,
      agency: "partial",
      diy: false,
    },
  ];

  const renderValue = (value: string | boolean, columnType: "waveWire" | "agency" | "diy") => {
    if (typeof value === "boolean") {
      return value ? (
        <Check className={`mx-auto ${columnType === "waveWire" ? "text-primary" : "text-green-500"}`} size={isMobile ? 14 : 22} />
      ) : (
        <X className="mx-auto text-destructive" size={isMobile ? 14 : 22} />
      );
    }
    if (value === "partial") {
      return <Minus className="mx-auto text-muted-foreground" size={isMobile ? 14 : 22} />;
    }
    return (
      <span className={`${columnType === "waveWire" ? "font-semibold text-foreground" : "text-muted-foreground"} ${isMobile ? "text-[9px]" : ""}`}>
        {value}
      </span>
    );
  };

  return (
    <section ref={sectionRef} className="py-8 md:py-16 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 bg-gradient-to-b from-card via-background to-card" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-primary/5 rounded-full blur-3xl" />

      <div className="container mx-auto px-3 md:px-6 relative">
        {/* Section Header */}
        <div className="text-center mb-6 md:mb-10">
          <div
            className={`inline-flex items-center gap-1.5 md:gap-2 px-3 md:px-4 py-1.5 md:py-2 bg-primary/10 border border-primary/30 rounded-full mb-3 md:mb-6 transition-all duration-700 ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            <Crown size={isMobile ? 14 : 18} className="text-primary" />
            <span className="text-xs md:text-sm font-bold text-primary uppercase tracking-wider">
              The Smarter Investment
            </span>
          </div>

          <h2
            className={`text-xl md:text-5xl lg:text-6xl font-black mb-3 md:mb-6 transition-all duration-700 delay-100 ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            Smarter Investment for <span className="text-primary">Your Business</span>
          </h2>

          <p
            className={`text-xs md:text-xl text-muted-foreground max-w-3xl mx-auto transition-all duration-700 delay-200 ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            <span className="text-foreground font-semibold">Flat rate</span> pricing, <span className="text-foreground font-semibold">no hidden fees</span>.
          </p>
        </div>

        {/* Comparison Table */}
        <div
          className={`max-w-5xl mx-auto transition-all duration-700 delay-300 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-16"
          }`}
        >
          <TooltipProvider delayDuration={200}>
          <div className="rounded-lg md:rounded-xl border border-border overflow-hidden bg-card/80 backdrop-blur-sm">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-b-2 border-border bg-muted/50">
                    <TableHead className={`${isMobile ? "w-[70px] text-[10px] py-2 px-1.5" : "w-[200px] py-4"} font-bold text-foreground`}>
                      Feature
                    </TableHead>
                    <TableHead className={`text-center ${isMobile ? "min-w-[60px] py-2 px-1" : "min-w-[160px] py-4"}`}>
                      <div className="flex flex-col items-center gap-0.5">
                        <div className="flex items-center gap-1">
                          <Crown size={isMobile ? 10 : 16} className="text-primary" />
                          <span className={`font-black text-primary ${isMobile ? "text-[9px]" : ""}`}>Wave & Wire</span>
                        </div>
                        {!isMobile && <span className="text-xs text-muted-foreground font-normal">The Smart Choice</span>}
                      </div>
                    </TableHead>
                    <TableHead className={`text-center ${isMobile ? "min-w-[60px] py-2 px-1" : "min-w-[160px] py-4"}`}>
                      <div className="flex flex-col items-center gap-0.5">
                        <span className={`font-bold text-foreground ${isMobile ? "text-[9px]" : ""}`}>Agency</span>
                        {!isMobile && <span className="text-xs text-muted-foreground font-normal">The Expensive Route</span>}
                      </div>
                    </TableHead>
                    <TableHead className={`text-center ${isMobile ? "min-w-[50px] py-2 px-1" : "min-w-[160px] py-4"}`}>
                      <div className="flex flex-col items-center gap-0.5">
                        <span className={`font-bold text-foreground ${isMobile ? "text-[9px]" : ""}`}>DIY</span>
                        {!isMobile && <span className="text-xs text-muted-foreground font-normal">Wix, Squarespace, etc.</span>}
                      </div>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {comparisonData.map((row, index) => (
                    <TableRow
                      key={row.feature}
                      className={`transition-colors ${
                        row.highlight
                          ? "bg-primary/10 hover:bg-primary/15"
                          : index % 2 === 0
                          ? "bg-background/50 hover:bg-muted/50"
                          : "hover:bg-muted/50"
                      }`}
                    >
                      <TableCell className={`${row.highlight ? "text-primary font-bold" : "text-foreground"} ${isMobile ? "text-[9px] py-1.5 px-1.5" : "font-medium"}`}>
                        {isMobile ? (
                          <span>{row.feature}</span>
                        ) : (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="inline-flex items-center gap-1.5 cursor-help group">
                                {row.feature}
                                <Info size={14} className="text-muted-foreground group-hover:text-primary transition-colors" />
                              </span>
                            </TooltipTrigger>
                            <TooltipContent side="right" className="max-w-[280px] text-sm">
                              <p>{row.tooltip}</p>
                            </TooltipContent>
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell className={`text-center ${row.highlight ? "bg-primary/20" : "bg-primary/5"} ${isMobile ? "py-1.5 px-1" : ""}`}>
                        {renderValue(row.waveWire, "waveWire")}
                      </TableCell>
                      <TableCell className={`text-center ${isMobile ? "py-1.5 px-1" : ""}`}>
                        {renderValue(row.agency, "agency")}
                      </TableCell>
                      <TableCell className={`text-center ${isMobile ? "py-1.5 px-1" : ""}`}>
                        {renderValue(row.diy, "diy")}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
          </TooltipProvider>

          {/* Bottom Note */}
          <div
            className={`mt-4 md:mt-8 text-center transition-all duration-700 delay-500 ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            <p className="text-[10px] md:text-sm text-muted-foreground">
              * Based on industry averages
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ComparisonTable;